import { oc } from 'ts-optchain';
import Config from 'react-native-config';

import * as StorageUtility from 'storage/recentSearches/storage';

import { RootState, RootDispatch } from 'store/types';
import * as geolocationSelectors from 'store/geolocation/selectors';
import * as profileSelectors from 'store/profile/selectors';
import * as myDocSelectors from 'store/myDoctors/selectors';
import * as globalActions from 'store/global/actions';
import * as accountSelectors from 'store/account/selectors';
import { TermsAndConditionsResponse } from 'store/global/reducers';

import * as authSelectors from 'store/authentication/selectors';
import * as profileActions from 'store/profile/actions';
import { KEY, ProviderFilterGroups, ProviderSearchOptions } from '../types';
import { currentLocation, versionNotFound, searchLimit } from '../constants';
import { orFallbackArray, orFallbackCoords } from '../utils';
import {
  insuranceIdsRequestDTO,
  filtersRequestDTO,
  facetsRequestDTO,
  storedPayloadDTO
} from './mappers';
import { ProviderSearchPayload, ProviderSearchRequest } from './types';

export const PROVIDER_SEARCH = `${KEY}.PROVIDER_SEARCH`;
export const PROVIDER_SEARCH_FAIL = `${KEY}.PROVIDER_SEARCH_FAIL`;
export const PROVIDER_SEARCH_SUCCESS = `${KEY}.PROVIDER_SEARCH_SUCCESS`;

export const PROVIDER_SEARCH_MORE = `${KEY}.PROVIDER_SEARCH_MORE`;
export const PROVIDER_SEARCH_MORE_FAIL = `${KEY}.PROVIDER_SEARCH_MORE_FAIL`;
export const PROVIDER_SEARCH_MORE_SUCCESS = `${KEY}.PROVIDER_SEARCH_MORE_SUCCESS`;

export const providerSearch = ({
  input,
  options,
  actionType = PROVIDER_SEARCH
}: ProviderSearchPayload) => (dispatch: RootDispatch, getState: () => RootState) => {
  const state = getState();

  const coords = geolocationSelectors.geolocationCoordsSelector(state);
  const currentConsumerId = accountSelectors.currentAccountConsumerIdSelector(state);
  const pcpIdList = myDocSelectors.currentConsumerPcpIdListSelector(state);
  const preferredIdList = myDocSelectors.currentConsumerPreferredIdListSelector(state);
  const pastIdList = myDocSelectors.currentConsumerPastIdListSelector(state);
  const isSelectHealth = profileSelectors.isSelectHealthMemberSelector(state);
  const insurances = profileSelectors.currentConsumerInsurancesSelector(state);

  const insuranceIds = insuranceIdsRequestDTO(insurances);
  const storedPayload = storedPayloadDTO({ input, options });
  const filters = filtersRequestDTO(options.filters, isSelectHealth);
  const facets = facetsRequestDTO(options.facets);

  const payload: ProviderSearchRequest = {
    filters,
    orderBy: options.orderBy,
    page: oc(options).page(0),
    limit: oc(options).limit(searchLimit),
    myPCPList: orFallbackArray(pcpIdList),
    myPastProvidersList: orFallbackArray(pastIdList),
    myPreferredProvidersList: orFallbackArray(preferredIdList)
  };

  if (payload.orderBy === 'acceptingNewPatients') {
    payload.filters?.push({ group: ProviderFilterGroups.hasOnlineNpvSlots, value: true });
    payload.orderBy = 'bestMatch';
  }

  if (orFallbackArray(facets, null)) {
    payload.facets = facets;
  }

  if (orFallbackArray(insuranceIds, null)) {
    payload.userInsuranceAliasValues = insuranceIds;
  }

  if (options.locationText && options.locationText !== currentLocation) {
    payload.locationText = options.locationText;
  } else {
    payload.location = {
      lat: orFallbackCoords(coords).latitude,
      lng: orFallbackCoords(coords).longitude
    };
  }

  if (input) {
    payload.input = input;

    if (currentConsumerId) {
      StorageUtility.addSearch(currentConsumerId, input, storedPayload);
    }
  }

  dispatch({
    type: actionType,
    payload: {
      client: 'azureProviderSearch',
      request: {
        method: 'post',
        url: `${Config.PROVIDER_URL_SEARCH}`,
        data: payload
      }
    }
  });
};

export const PROVIDER_SEARCH_TERMS_SHOWN = `${KEY}.PROVIDER_SEARCH_TERMS_SHOWN`;

export const setSearchTermsShown = () => ({ type: PROVIDER_SEARCH_TERMS_SHOWN });

export const acceptSearchTermConditions = ({ terms }: { terms?: TermsAndConditionsResponse }) => (
  dispatch: RootDispatch,
  getState: () => RootState
) => {
  const state = getState();
  const consumer = profileSelectors.profileConsumerSelector(state);
  const isAuthenticated = authSelectors.isDfdAuthenticatedSelector(state);

  dispatch(globalActions.setAcceptTemporaryTC('PROVIDER_SEARCH'));

  if (isAuthenticated) {
    const searchTermsAcceptance = {
      version: terms?.version || versionNotFound,
      date: new Date(),
      type: terms?.type || versionNotFound
    };

    const externalTerms = consumer?.externalTermsAcceptance || [];
    const updateTermsArray = externalTerms.concat(searchTermsAcceptance);

    dispatch(
      profileActions.updateConsumerProfile(
        consumerData => ({
          ...consumerData,
          externalTermsAcceptance: updateTermsArray
        }),
        ['ExternalTermsAcceptance']
      )
    );
  }
};

export const RESET = `${KEY}.RESET`;

export const reset = () => ({ type: RESET });

export const SET_FILTER_OPTIONS = `${KEY}.SET_FILTER_OPTIONS`;

export const setFilterOptions = (options: ProviderSearchOptions) => (dispatch: RootDispatch) => {
  dispatch({ type: SET_FILTER_OPTIONS, payload: options });
};
